import type { ColorSpec } from './types';

/**
 * Cream color spec — uses the `cream` Tailwind palette.
 *
 * Modeled on yellow because the palette is bright: filled buttons use
 * a light bg (-200 = #ece6da, matching the "Add thumbnail" design) with
 * dark text, not the dark-bg + white-text pattern used by blue / red /
 * green / purple / forest.
 */
export const cream: ColorSpec = {
  filled: {
    border: 'border-cream-300 dark:border-cream-600',
    bg: 'bg-cream-200 text-cream-800 dark:bg-cream-800 dark:text-cream-200',
    bgActive: 'bg-cream-400 text-cream-900 dark:bg-cream-500 dark:text-charade-950',
    hover: 'hover:bg-cream-300 hover:text-cream-900 active:bg-cream-400 active:text-cream-900',
    hoverDark: 'dark:hover:bg-cream-700 dark:hover:text-cream-100 dark:active:bg-cream-600',
  },
  outline: {
    border: 'border-cream-400 dark:border-cream-600',
    bg: 'bg-cream-50 dark:bg-cream-900 text-cream-800 dark:text-cream-200',
    bgActive: 'bg-cream-500 dark:bg-cream-400 text-white dark:text-charade-950',
    hover: 'hover:bg-cream-100 hover:text-cream-900 active:bg-cream-200 active:text-cream-900',
    hoverDark: 'dark:hover:bg-cream-800 dark:hover:text-cream-100 dark:active:bg-cream-700 dark:active:text-cream-100',
    hoverWhenActive: 'hover:bg-cream-600 dark:hover:bg-cream-500',
  },
  soft: {
    border: 'border-cream-100 dark:border-cream-800',
    bg: 'bg-cream-50 dark:bg-cream-900 text-cream-800 dark:text-cream-200 font-normal',
    bgActive: 'bg-cream-500 dark:bg-cream-400 text-white dark:text-charade-950',
    hover: 'hover:bg-cream-500 hover:text-white active:bg-cream-600 active:text-white',
    hoverDark:
      'dark:hover:bg-cream-400 dark:hover:text-charade-950 dark:active:bg-cream-500 dark:active:text-charade-950',
    hoverWhenActive: 'hover:bg-cream-600 dark:hover:bg-cream-500',
  },
  textFilled: {
    text: 'text-cream-800 dark:text-cream-300',
    activeBg: 'bg-cream-300 text-cream-900',
    hover: 'hover:bg-cream-200 active:bg-cream-300',
    // Cream filled has no dark hover override (mirrors yellow). Keep empty.
    hoverDark: '',
  },
  textSoft: {
    text: 'text-cream-800 dark:text-cream-200 font-normal',
    activeBg: 'bg-cream-500 text-white dark:bg-cream-400 dark:text-cream-950',
    hover: 'hover:bg-cream-100 hover:text-cream-900 active:bg-cream-200',
    hoverDark: 'dark:hover:bg-cream-900 dark:hover:text-cream-100 dark:active:bg-cream-800',
  },
  tooltip: 'bg-cream-200 dark:bg-cream-700 text-cream-800 dark:text-cream-100',
  badge: {
    filled: 'bg-cream-600 dark:bg-cream-700 text-white',
    soft: 'bg-cream-400 dark:bg-cream-500 text-white',
  },
  softIcon: 'text-cream-800 dark:text-cream-200 group-hover/btn:text-current',
  underline: {
    filled: '[&_u]:text-cream-900 dark:[&_u]:text-cream-200 [&_u]:decoration-cream-700 dark:[&_u]:decoration-cream-300',
    outline:
      '[&_u]:text-cream-900 dark:[&_u]:text-cream-300 [&_u]:decoration-cream-700 dark:[&_u]:decoration-cream-400',
    soft: '[&_u]:text-cream-900 dark:[&_u]:text-cream-300 [&_u]:decoration-cream-400 dark:[&_u]:decoration-cream-400',
  },
  input: {
    filled: {
      border: 'border-cream-400 dark:border-cream-500',
      bg: 'bg-cream-200 dark:bg-cream-700',
      focusBg: 'focus-within:bg-cream-300 dark:focus-within:bg-cream-600',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-cream-400 dark:focus-within:outline-cream-500',
      hoverActive:
        'hover:bg-cream-300 hover:text-cream-900 active:bg-cream-400 dark:hover:bg-cream-600 dark:hover:text-cream-100 dark:active:bg-cream-500',
      readOnlyBg: 'bg-cream-200 dark:bg-cream-800',
      text: 'text-cream-900 dark:text-cream-100',
    },
    outline: {
      border: 'border-cream-400 dark:border-cream-600',
      bg: 'bg-cream-50 dark:bg-cream-900',
      focusBg: 'focus-within:bg-cream-100 dark:focus-within:bg-cream-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-cream-400 dark:focus-within:outline-cream-600',
      hoverActive:
        'hover:bg-cream-100 hover:text-cream-900 active:bg-cream-200 active:text-cream-900 dark:hover:bg-cream-800 dark:hover:text-cream-100 dark:active:bg-cream-700 dark:active:text-cream-100',
      readOnlyBg: 'bg-cream-50 dark:bg-cream-900',
      text: 'text-cream-800 dark:text-cream-200',
    },
    soft: {
      border: 'border-cream-100 dark:border-cream-800',
      bg: 'bg-cream-50 dark:bg-cream-900',
      focusBg: 'focus-within:bg-cream-100 dark:focus-within:bg-cream-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-cream-100 dark:focus-within:outline-cream-800',
      hoverActive:
        'hover:bg-cream-100 hover:text-cream-900 active:bg-cream-200 active:text-cream-950 dark:hover:bg-cream-800 dark:hover:text-cream-100 dark:active:bg-cream-700 dark:active:text-cream-100',
      readOnlyBg: 'bg-cream-50 dark:bg-cream-900',
      text: 'text-cream-800 dark:text-cream-200',
    },
  },
  inputMuted: {
    filled: 'text-cream-800',
    outline: 'text-cream-400 dark:text-cream-600',
    soft: 'text-cream-400 dark:text-cream-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-cream-800',
    outline: 'placeholder:text-cream-400 dark:placeholder:text-cream-600',
    soft: 'placeholder:text-cream-400 dark:placeholder:text-cream-600',
  },
  group: {
    filled: 'border-cream-300 dark:border-charade-600 bg-cream-100 dark:bg-charade-800',
    outline: 'border-cream-400 dark:border-cream-600 bg-cream-50 dark:bg-charade-900',
    soft: 'border-cream-200 dark:border-charade-700 bg-cream-100 dark:bg-charade-800',
  },
  groupDivider: {
    filled: 'bg-cream-300 dark:bg-charade-600',
    outline: 'bg-cream-300 dark:bg-cream-700',
    soft: 'bg-cream-200 dark:bg-charade-600',
  },
  segment: {
    filled: 'border-cream-300 dark:border-charade-600 bg-cream-100 dark:bg-charade-800',
    outline: 'border-cream-400 dark:border-cream-600 bg-cream-50 dark:bg-charade-900',
  },
  segmentDivider: {
    filled: 'bg-cream-300 dark:bg-charade-600',
    outline: 'bg-cream-300 dark:bg-cream-700',
  },
  popoverHighlight: 'bg-cream-300 text-cream-900',
  popoverText: 'text-cream-800 dark:text-cream-200',
  popoverBorder: 'border-cream-200 dark:border-cream-700',
};
